import { useEffect, useRef, useState } from "react";

export function PortalLayout({
  eyebrow,
  title,
  description,
  backHref,
  children,
  heroAside = null,
  heroAction = null,
  heroClassName = "",
  heroContentClassName = "",
  titleClassName = "",
  heroAsideClassName = "",
}) {
  return (
    <main className="app-shell">
      <section className={`hero portal-hero ${heroClassName}`.trim()}>
        <div className={heroContentClassName}>
          <p className="eyebrow">{eyebrow}</p>
          <h1 className={titleClassName}>{title}</h1>
          {description ? <p className="hero-copy">{description}</p> : null}
        </div>
        <div className={`portal-hero-side ${heroAsideClassName}`.trim()}>
          {heroAside}
          {heroAction ? <div className="hero-action">{heroAction}</div> : null}
          {backHref ? (
            <button className="secondary-button" type="button" onClick={() => { window.location.hash = backHref; }}>
              Volver
            </button>
          ) : null}
        </div>
      </section>
      {children}
    </main>
  );
}

export function PortalCard({ eyebrow, title, description, items, href }) {
  return (
    <article className="portal-card">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      <p>{description}</p>
      <ul className="portal-list">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <a className="submit-button portal-link" href={href}>
        Ingresar
      </a>
    </article>
  );
}

export function PortalListItem({ title, description }) {
  return (
    <div className="portal-list-card">
      <strong>{title}</strong>
      {description ? <p>{description}</p> : null}
    </div>
  );
}

export function FormCard({ title, description, onSubmit, children, className = "" }) {
  return (
    <form className={`panel ${className}`.trim()} onSubmit={onSubmit}>
      <div className="panel-heading">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <div className="panel-body">{children}</div>
    </form>
  );
}

export function InfoCard({ title, rows }) {
  return (
    <section className="detail-panel">
      <div className="detail-panel-header">
        <h2>{title}</h2>
      </div>
      <div className="detail-table">
        {rows.map((row) => (
          <div className="detail-row" key={`${title}-${row.label}`}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ChoferSessionSummary({
  name = "Chofer",
  cuit = "",
  email = "",
  loginInfo = null,
  fallbackLoginMethod = "",
  fallbackLoginStatus = "",
  loginMethodEmail = "",
}) {
  return (
    <div className="session-summary chofer-session-summary">
      <div className="session-summary-row">
        <span>Chofer</span>
        <strong>{name || "Chofer"}</strong>
      </div>
      <div className="session-summary-row">
        <span>CUIT</span>
        <strong>{cuit || "-"}</strong>
      </div>
      <div className="session-summary-row">
        <span>Email</span>
        <strong>{loginInfo?.email || email || "-"}</strong>
      </div>
      {loginInfo?.email ? (
        <div className="session-summary-row">
          <span>Cuenta activa</span>
          <strong>{loginInfo?.email}</strong>
        </div>
      ) : null}
      {getLoginDisplayInfo(loginInfo, fallbackLoginMethod, fallbackLoginStatus) ? (
        <>
          <div className="session-summary-row">
            <span>Login</span>
            <strong>{formatLoginStatus(loginInfo, fallbackLoginStatus)}</strong>
          </div>
          <div className="session-summary-row">
            <span>Método</span>
            <strong>{formatLoginMethod(loginInfo, fallbackLoginMethod)}</strong>
          </div>
          <div className="session-summary-row">
            <span>Último ingreso</span>
            <strong>{formatDateTime(loginInfo?.last_login_at)}</strong>
          </div>
        </>
      ) : null}
    </div>
  );
}

function getLoginDisplayInfo(loginInfo, fallbackMethod, fallbackStatus) {
  if (loginInfo) {
    return loginInfo;
  }

  if (fallbackMethod) {
    return {
      method: fallbackMethod,
      status: fallbackStatus || "Pendiente",
    };
  }

  return null;
}

function formatLoginMethod(loginInfo, fallbackMethod) {
  const info = getLoginDisplayInfo(loginInfo, fallbackMethod, "");
  if (!info) {
    return "-";
  }

  const method = String(info.method || info.provider || "").toLowerCase();

  if (!method || method === "local" || method === "email") {
    return "Email";
  }

  if (method.includes("google")) {
    return "Google";
  }

  if (method.includes("facebook")) {
    return "Facebook";
  }

  return method;
}

function translateUiToken(value) {
  const normalized = String(value || "").trim().toLowerCase();
  const map = {
    active: "Activo",
    inactive: "Inactivo",
    pending: "Pendiente",
    pending_email_verification: "Pendiente de verificación",
    pending_validation: "Pendiente de validación",
    pending_signature: "Pendiente de firma",
    pending_acceptance: "Pendiente de aceptación",
    approved: "Aprobado",
    rejected: "Rechazado",
    observed: "Observado",
    validated: "Validado",
    access_denied: "Acceso denegado",
    access_granted: "Acceso aprobado",
    revoked: "Revocado",
    denied: "Denegado",
    disabled: "Deshabilitado",
    local: "Email",
    email: "Email",
    email_code: "Código por email",
    whatsapp_code: "Código por WhatsApp",
  };

  if (map[normalized]) {
    return map[normalized];
  }

  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatLoginStatus(loginInfo, fallbackStatus) {
  if (loginInfo) {
    if (loginInfo.email_verified) {
      return "Activo";
    }

    if (loginInfo.status === "pending_email_verification") {
      return "Pendiente de verificación";
    }

    if (loginInfo.status === "pending") {
      return "Pendiente";
    }

    if (loginInfo.status) {
      return translateUiToken(loginInfo.status);
    }

    return "Sin acceso";
  }

  return fallbackStatus ? translateUiToken(fallbackStatus) : "Sin acceso";
}

export function ExpandableInfoCard({ title, rows, badge = null, summary = "" }) {
  return (
    <details className="detail-panel expandable-card">
      <summary className="expandable-card-summary">
        <div>
          <h2>{title}</h2>
          {summary ? <p>{summary}</p> : null}
        </div>
        <div className="expandable-card-summary-side">
          {badge}
          <span className="expandable-card-chevron" aria-hidden="true">
            ▾
          </span>
        </div>
      </summary>
      <div className="detail-table expandable-card-body">
        {rows.map((row) => (
          <div className="detail-row" key={`${title}-${row.label}`}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </div>
        ))}
      </div>
    </details>
  );
}

export function FeedbackPanel({ message, error = false }) {
  return (
    <section className={`detail-feedback ${error ? "detail-feedback-error" : ""}`}>
      <p>{message}</p>
    </section>
  );
}

export function ProcessingPanel({ title = "Procesando", message }) {
  return (
    <section className="detail-feedback processing-panel">
      <div className="processing-panel-body">
        <span className="processing-spinner" aria-hidden="true" />
        <div>
          <strong>{title}</strong>
          <p>{message}</p>
        </div>
      </div>
    </section>
  );
}

export function CaptureResultCard({ label, file, emptyMessage = "Sin captura confirmada." }) {
  return (
    <div className="portal-list-card">
      <strong>{label}</strong>
      {file ? (
        <>
          <p>Archivo listo para upload: {file.name}</p>
          <p>Tipo: {file.type}</p>
          <p>Tamano: {file.size} bytes</p>
        </>
      ) : (
        <p>{emptyMessage}</p>
      )}
    </div>
  );
}

export function VehicleFields({ value, onChange }) {
  return (
    <>
      <Input
        label="CUIT titular"
        value={value.titular_cuit}
        onChange={(fieldValue) => onChange({ ...value, titular_cuit: fieldValue })}
      />
      <Input label="Dominio" value={value.dominio} onChange={(fieldValue) => onChange({ ...value, dominio: fieldValue })} />
      <Input label="Marca" value={value.marca} onChange={(fieldValue) => onChange({ ...value, marca: fieldValue })} />
      <Input label="Modelo" value={value.modelo} onChange={(fieldValue) => onChange({ ...value, modelo: fieldValue })} />
      <Input label="Anio" value={value.anio} onChange={(fieldValue) => onChange({ ...value, anio: fieldValue })} />
      <Input label="Tipo" value={value.tipo} onChange={(fieldValue) => onChange({ ...value, tipo: fieldValue })} />
    </>
  );
}

export function Input({ label, value, onChange, type = "text", disabled = false, autoComplete, onBlur, placeholder }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type={type}
        value={value}
        disabled={disabled}
        autoComplete={autoComplete}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />
    </label>
  );
}

export function TextArea({ label, value, onChange }) {
  return (
    <label className="field">
      <span>{label}</span>
      <textarea rows="3" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

export function FileInput({ label, onChange }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type="file" onChange={(event) => onChange(event.target.files?.[0] || null)} />
    </label>
  );
}

export function StatusBadge({ label, tone, large = false, details = null, loading = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!details || !isOpen) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [details, isOpen]);

  const content = (Array.isArray(details) ? details : details ? [details] : [])
    .filter((item) => item !== null && item !== undefined && item !== "")
    .map((item) => normalizeStatusDetail(item))
    .filter(Boolean);

  if (!content.length) {
    return (
      <span className={`status-badge status-badge-${tone} ${large ? "status-badge-large" : ""}`}>
        {loading ? <span className="status-badge-spinner" aria-hidden="true" /> : <span className="status-badge-dot" aria-hidden="true" />}
        <span>{label}</span>
      </span>
    );
  }

  return (
    <span
      ref={rootRef}
      className={`status-badge-wrapper ${isOpen ? "status-badge-wrapper-open" : ""}`}
    >
      <button
        className={`status-badge status-badge-${tone} ${large ? "status-badge-large" : ""} status-badge-button`}
        type="button"
        onClick={() => setIsOpen((current) => !current)}
      >
        {loading ? <span className="status-badge-spinner" aria-hidden="true" /> : <span className="status-badge-dot" aria-hidden="true" />}
        <span>{label}</span>
      </button>
      {isOpen ? (
        <div className="status-badge-popover">
          {content.map((item, index) => (
            <p key={`${label}-${index}`}>{item}</p>
          ))}
        </div>
      ) : null}
    </span>
  );
}

function normalizeStatusDetail(value) {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeStatusDetail(item)).filter(Boolean).join(" · ");
  }

  if (typeof value === "object") {
    const preferred = [value.message, value.reason, value.label].find((item) => typeof item === "string" && item.trim());
    if (preferred) {
      return preferred;
    }

    try {
      return JSON.stringify(value);
    } catch (_error) {
      return "Detalle de validación disponible.";
    }
  }

  return "";
}

export function StatusMessage({ message }) {
  if (!message) {
    return null;
  }

  return <p className="status-message">{message}</p>;
}

export function BiometryResultCard({ result }) {
  return (
    <section className="detail-panel biometria-result-card">
      <div className="detail-panel-header">
        <h2>Resultado biometrico</h2>
        <StatusBadge label={result.validado ? "Aprobado" : "Observado"} tone={result.validado ? "vigente" : "vencido"} />
      </div>
      <div className="detail-table">
        <div className="detail-row">
          <span>Chofer cuit</span>
          <strong>{result.chofer_cuit}</strong>
        </div>
        <div className="detail-row">
          <span>Similaridad</span>
          <strong>{Number(result.similaridad).toFixed(2)}%</strong>
        </div>
        <div className="detail-row">
          <span>Resultado</span>
          <strong>{result.validado ? "Validado" : "No validado"}</strong>
        </div>
        <div className="detail-row">
          <span>Fecha</span>
          <strong>{result.created_at ? formatDateTime(result.created_at) : "-"}</strong>
        </div>
      </div>
    </section>
  );
}

export function LastBiometryCard({ biometria }) {
  return (
    <section className="detail-panel biometria-result-card">
      <div className="detail-panel-header">
        <h2>Ultima validacion guardada</h2>
        <StatusBadge label={biometria.validado ? "Validado" : "Rechazado"} tone={biometria.validado ? "vigente" : "vencido"} />
      </div>
      <div className="detail-table">
        <div className="detail-row">
          <span>Chofer cuit</span>
          <strong>{biometria.chofer_cuit}</strong>
        </div>
        <div className="detail-row">
          <span>Similitud</span>
          <strong>{Number(biometria.similitud).toFixed(2)}%</strong>
        </div>
        <div className="detail-row">
          <span>Fecha</span>
          <strong>{biometria.created_at ? formatDateTime(biometria.created_at) : "-"}</strong>
        </div>
      </div>
    </section>
  );
}

export function OcrResultCard({ title, data }) {
  return (
    <section className="detail-panel biometria-result-card">
      <div className="detail-panel-header">
        <h2>{title}</h2>
        <StatusBadge label={data.is_dni_argentina ? "DNI detectado" : "Revisar captura"} tone={data.is_dni_argentina ? "vigente" : "faltante"} />
      </div>
      <div className="detail-table">
        <div className="detail-row">
          <span>Lado</span>
          <strong>{data.lado || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>CUIT</span>
          <strong>{data.datos_extraidos?.cuit || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Nombre</span>
          <strong>{data.datos_extraidos?.nombre || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Apellido</span>
          <strong>{data.datos_extraidos?.apellido || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Nro DNI</span>
          <strong>{data.datos_extraidos?.nro_dni || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Fecha nacimiento</span>
          <strong>{data.datos_extraidos?.fecha_nacimiento || "-"}</strong>
        </div>
      </div>
      {data.observaciones?.length ? (
        <div className="portal-list">
          {data.observaciones.map((item) => (
            <PortalListItem key={item} title="Observacion OCR" description={item} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function LicenciaOcrResultCard({ data }) {
  const hasMissingFields = Array.isArray(data.missing_fields) && data.missing_fields.length > 0;
  const badgeTone = data.is_licencia_conducir_argentina ? (hasMissingFields ? "faltante" : "vigente") : "vencido";
  const badgeLabel = data.is_licencia_conducir_argentina
    ? hasMissingFields
      ? "Licencia con revision"
      : "Licencia detectada"
    : "Revisar captura";

  return (
    <section className="detail-panel biometria-result-card">
      <div className="detail-panel-header">
        <h2>OCR licencia argentina</h2>
        <StatusBadge label={badgeLabel} tone={badgeTone} />
      </div>
      <div className="detail-table">
        <div className="detail-row">
          <span>Apellido</span>
          <strong>{data.fields?.apellido?.value || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Nombre</span>
          <strong>{data.fields?.nombre?.value || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>DNI</span>
          <strong>{data.fields?.dni?.value || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Nro licencia</span>
          <strong>{data.fields?.numero_licencia?.value || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Fecha nacimiento</span>
          <strong>{data.fields?.fecha_nacimiento?.value || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Otorgamiento</span>
          <strong>{data.fields?.fecha_otorgamiento?.value || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Vencimiento</span>
          <strong>{data.fields?.fecha_vencimiento?.value || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Clases</span>
          <strong>{Array.isArray(data.fields?.clases?.value) ? data.fields.clases.value.join(", ") : "-"}</strong>
        </div>
      </div>
      {data.observaciones?.length ? (
        <div className="portal-list">
          {data.observaciones.map((item) => (
            <PortalListItem key={item} title="Observacion OCR" description={item} />
          ))}
        </div>
      ) : null}
      {data.missing_fields?.length ? (
        <div className="portal-list">
          <PortalListItem title="Campos faltantes" description={data.missing_fields.join(", ")} />
        </div>
      ) : null}
    </section>
  );
}

export function ExtractedDriverCard({ data }) {
  return (
    <section className="detail-panel biometria-result-card">
      <div className="detail-panel-header">
        <h2>Datos extraidos del DNI</h2>
      </div>
      <div className="detail-table">
        <div className="detail-row">
          <span>CUIT</span>
          <strong>{data.cuit || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Nombre</span>
          <strong>{data.nombre || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Apellido</span>
          <strong>{data.apellido || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Nro DNI</span>
          <strong>{data.nro_dni || "-"}</strong>
        </div>
        <div className="detail-row">
          <span>Fecha nacimiento</span>
          <strong>{data.fecha_nacimiento || "-"}</strong>
        </div>
      </div>
    </section>
  );
}

export function buildBiometryErrorMessage(message, choferCuit) {
  if (message.includes("Chofer") && message.includes("no existe")) {
    return `El chofer ${choferCuit} no existe todavia. Guarda el alta antes de validar biometria.`;
  }

  if (message.includes("Se requieren dni_frente, dni_dorso y selfie")) {
    return "Faltan capturas requeridas. Confirma frente, dorso y selfie antes de continuar.";
  }

  if (message.includes("S3_BUCKET is required")) {
    return "La configuracion de S3 no esta completa en el backend local.";
  }

  if (message.includes("Biometric timeout")) {
    return "La validacion biometrica demoro demasiado. Reintenta la selfie.";
  }

  if (message.includes("AccessDenied") || message.includes("InvalidAccessKeyId") || message.includes("SignatureDoesNotMatch")) {
    return "AWS rechazo la operacion. Revisa credenciales, permisos IAM y la region configurada.";
  }

  if (message.includes("No se detecto una cara util en el DNI")) {
    return "No pudimos detectar bien la cara en el DNI. Reintenta la foto del frente con mejor luz y foco.";
  }

  if (message.includes("No se detecto una cara util en la selfie")) {
    return "No pudimos detectar bien la cara en la selfie. Reintenta mirando de frente y con buena luz.";
  }

  if (message.includes("InvalidImageFormatException")) {
    return "Rekognition rechazo la imagen. Repite la captura con mejor foco e iluminacion.";
  }

  return message;
}

export function resetIdentityFlow(
  setStep,
  setDniFrenteFile,
  setDniDorsoFile,
  setSelfieFile,
  setIdentityData,
  setVerificationResult,
  setFlowMessage
) {
  setStep(1);
  setDniFrenteFile(null);
  setDniDorsoFile(null);
  setSelfieFile(null);
  setIdentityData(null);
  setVerificationResult(null);
  setFlowMessage("");
}

const NAME_NOISE_TOKENS = [
  "REPUBLICA ARGENTINA",
  "MERCOSUR",
  "DOCUMENTO NACIONAL DE IDENTIDAD",
];
const NAME_NOISE_PATTERNS = NAME_NOISE_TOKENS.map((token) => new RegExp(token.replace(/\s+/g, "\\s*"), "gi"));

export function mergeDniRegistroData(front, back) {
  const frontData = front?.datos_extraidos || {};
  const backData = back?.datos_extraidos || {};
  const frontFields = front?.campos || {};
  const backFields = back?.campos || {};

  const frontFirstName = sanitizeNameValue(frontData.nombre);
  const backFirstName = sanitizeNameValue(backData.nombre);
  const frontLastName = sanitizeNameValue(frontData.apellido);
  const backLastName = sanitizeNameValue(backData.apellido);

  const frontFull = buildFullName(frontFirstName, frontLastName);
  const backFull = buildFullName(backFirstName, backLastName);
  const fallbackFull =
    sanitizeNameValue(`${frontData.nombre || ""} ${frontData.apellido || ""}`) ||
    sanitizeNameValue(`${backData.nombre || ""} ${backData.apellido || ""}`);

  const cleanedFullName = frontFull || backFull || fallbackFull;
  const { first: derivedFirst, last: derivedLast } = splitNameParts(
    cleanedFullName || frontFirstName || backFirstName || ""
  );

  const firstName = frontFirstName || backFirstName || derivedFirst;
  const lastName = frontLastName || backLastName || derivedLast;
  const fullName = cleanedFullName || buildFullName(firstName, lastName);

  const dniNumber = normalizeDigits(
    pickValue(
      getFieldValue(frontFields, "dni"),
      getFieldValue(backFields, "dni"),
      frontData.nro_dni,
      backData.nro_dni
    )
  );

  const birthDate = pickValue(
    getFieldValue(frontFields, "fecha_nacimiento"),
    getFieldValue(backFields, "fecha_nacimiento"),
    frontData.fecha_nacimiento,
    backData.fecha_nacimiento
  );

  const sex = pickValue(
    getFieldValue(frontFields, "sexo"),
    getFieldValue(backFields, "sexo"),
    frontData.sexo,
    backData.sexo
  );

  const issueDate = pickValue(
    getFieldValue(frontFields, "fecha_emision"),
    getFieldValue(backFields, "fecha_emision")
  );
  const expiryDate = pickValue(
    getFieldValue(frontFields, "fecha_vencimiento"),
    getFieldValue(backFields, "fecha_vencimiento")
  );
  const tramiteValue = pickValue(
    getFieldValue(frontFields, "tramite"),
    getFieldValue(backFields, "tramite"),
    frontData.tramite,
    backData.tramite
  );

  return {
    cuit: pickValue(
      frontData.cuit,
      backData.cuit,
      getFieldValue(frontFields, "cuit"),
      getFieldValue(backFields, "cuit")
    ),
    nombre: firstName,
    apellido: lastName,
    full_name: fullName,
    first_name: firstName,
    last_name: lastName,
    nro_dni: dniNumber,
    dni_number: dniNumber,
    sex,
    fecha_nacimiento: birthDate,
    birth_date: birthDate,
    issue_date: issueDate,
    expiry_date: expiryDate,
    tramite: tramiteValue,
    raw_ocr: {
      frente: front?.raw_ocr,
      dorso: back?.raw_ocr,
    },
  };
}

function sanitizeNameValue(value) {
  const raw = `${value || ""}`.trim();
  if (!raw) {
    return "";
  }

  let cleaned = raw;
  NAME_NOISE_PATTERNS.forEach((pattern) => {
    cleaned = cleaned.replace(pattern, " ");
  });

  cleaned = cleaned.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ\s'-]+/g, " ");
  cleaned = cleaned.replace(/\s+/g, " ").trim();

  return toTitleCase(cleaned);
}

function toTitleCase(value) {
  return value
    .split(" ")
    .filter(Boolean)
    .map((chunk) => `${chunk.charAt(0).toUpperCase()}${chunk.slice(1).toLowerCase()}`)
    .join(" ");
}

function splitNameParts(fullName) {
  const parts = (fullName || "").split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return { first: "", last: "" };
  }

  if (parts.length === 1) {
    return { first: parts[0], last: parts[0] };
  }

  const last = parts.pop();
  return { first: parts.join(" "), last };
}

function buildFullName(first, last) {
  return [first, last].filter(Boolean).join(" ").trim();
}

function getFieldValue(record, key) {
  const field = record?.[key];
  if (!field) {
    return "";
  }

  if (typeof field === "string") {
    return field.trim();
  }

  return `${field.value || ""}`.trim();
}

function pickValue(...values) {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }
    const normalized = typeof value === "string" ? value.trim() : String(value).trim();
    if (normalized) {
      return normalized;
    }
  }
  return "";
}

function normalizeDigits(value) {
  return `${value || ""}`.replace(/\D+/g, "");
}

export function buildRows(record, preferredOrder, labels = {}) {
  if (!record) {
    return [];
  }

  const keys = preferredOrder || Object.keys(record);
  return keys
    .filter((key) => record[key] !== undefined)
    .map((key) => ({
      label: labels[key] || humanizeLabel(key),
      value: formatDisplayValue(key, record[key]),
    }));
}

export function humanizeLabel(label) {
  const mapped = {
    cuit: "CUIT",
    chofer_cuit: "CUIT del chofer",
    nro_dni: "Nro DNI",
    created_at: "Fecha de alta",
    updated_at: "Fecha de actualizacion",
    fecha_nacimiento: "Fecha de nacimiento",
    fecha_vencimiento: "Fecha de vencimiento",
    database_time: "Fecha de verificacion",
    app_env: "Entorno",
  };

  return mapped[label] || label.replace(/_/g, " ");
}

export function formatDate(value) {
  const normalized = normalizeDateValue(value);
  return normalized || "-";
}

export function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function formatDisplayValue(key, value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (isDateLikeField(key)) {
    return formatDate(value);
  }

  return String(value);
}

function isDateLikeField(key) {
  return /(^fecha_|_at$|_date$|date$)/i.test(String(key));
}

function normalizeDateValue(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
